import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useIndexerPaths } from '../lib/indexer-paths';
import { getIndexerErrorInfo } from '../api/errors';
import {
  useGetProjectGoogleCredentialsQuery,
  useListIndexSchedulesQuery,
  useListInspectSchedulesQuery,
} from '../api/indexerApi';
import type { ScheduleMessenger } from '../api/types';
import { formatScheduleMessengersLabel } from './ScheduleMessengerSelect';
import { IndexerIcon } from './IndexerIcon';
import { OverviewSettingsChecklistPreload } from './preloads/IndexerPreloads';

type CheckTone = 'ok' | 'warn' | 'muted';

type ChecklistItem = {
  id: string;
  ok: boolean;
  title: string;
  detail: string;
  href: string;
  actionLabel: string;
};

function messengersOnEnabled(
  schedules: { enabled: boolean; messengers: ScheduleMessenger[] }[],
): ScheduleMessenger[] {
  const set = new Set<ScheduleMessenger>();
  for (const schedule of schedules) {
    if (!schedule.enabled) continue;
    for (const messenger of schedule.messengers ?? []) {
      set.add(messenger);
    }
  }
  return [...set];
}

export function OverviewSettingsChecklist({ projectId }: { projectId: string }) {
  const paths = useIndexerPaths();
  const {
    data: indexData,
    isFetching: indexLoading,
    isError: indexError,
    error: indexErr,
  } = useListIndexSchedulesQuery(projectId);
  const {
    data: inspectData,
    isFetching: inspectLoading,
    isError: inspectError,
    error: inspectErr,
  } = useListInspectSchedulesQuery(projectId);
  const {
    data: google,
    isFetching: googleLoading,
    isError: googleError,
    error: googleErr,
  } = useGetProjectGoogleCredentialsQuery(projectId);

  const loading = indexLoading || inspectLoading || googleLoading;
  const error =
    indexError || inspectError || googleError
      ? getIndexerErrorInfo(indexErr ?? inspectErr ?? googleErr).message ||
        'بارگذاری خلاصهٔ تنظیمات ناموفق بود'
      : null;

  const indexSchedules = indexData?.schedules ?? [];
  const inspectSchedules = inspectData?.schedules ?? [];
  const indexEnabled = indexSchedules.some((item) => item.enabled);
  const inspectEnabled = inspectSchedules.some((item) => item.enabled);
  const activeMessengers = messengersOnEnabled([
    ...indexSchedules,
    ...inspectSchedules,
  ]);
  const googleConfigured = google?.configured ?? false;

  const messengerOk = activeMessengers.length > 0;
  const messengerDetail = messengerOk
    ? `فعال: ${formatScheduleMessengersLabel(activeMessengers)}`
    : indexEnabled || inspectEnabled
      ? 'در هیچ زمان‌بندی فعالی تلگرام یا بله تیک نخورده'
      : 'اول یک زمان‌بندی فعال بسازید، بعد کانال اطلاع‌رسانی را انتخاب کنید';

  const items: ChecklistItem[] = useMemo(
    () => [
      {
        id: 'inspect-schedule',
        ok: inspectEnabled,
        title: 'زمان‌بندی فعال برای وضعیت ایندکس',
        detail: inspectEnabled
          ? 'حداقل یک زمان‌بندی بازرسی روشن است'
          : 'هنوز زمان‌بندی فعالی برای URL Inspection ندارید',
        href: paths.urlInspection,
        actionLabel: inspectEnabled ? 'مشاهده' : 'تنظیم',
      },
      {
        id: 'index-schedule',
        ok: indexEnabled,
        title: 'زمان‌بندی فعال برای درخواست ایندکس',
        detail: indexEnabled
          ? 'حداقل یک زمان‌بندی درخواست ایندکس روشن است'
          : 'هنوز زمان‌بندی فعالی برای Indexing API ندارید',
        href: paths.index,
        actionLabel: indexEnabled ? 'مشاهده' : 'تنظیم',
      },
      {
        id: 'messengers',
        ok: messengerOk,
        title: 'اطلاع‌رسانی‌ها',
        detail: messengerDetail,
        href: inspectEnabled ? paths.urlInspection : paths.index,
        actionLabel: messengerOk ? 'مشاهده' : 'تنظیم',
      },
      {
        id: 'google',
        ok: googleConfigured,
        title: 'تنظیمات گوگل (Indexing / Search Console)',
        detail: googleConfigured
          ? 'کلید سرویس‌اکانت برای این پروژه آپلود شده'
          : 'JSON سرویس‌اکانت هنوز آپلود نشده',
        href: paths.settings,
        actionLabel: googleConfigured ? 'مشاهده' : 'تنظیم',
      },
    ],
    [
      inspectEnabled,
      indexEnabled,
      messengerOk,
      messengerDetail,
      googleConfigured,
      paths,
    ],
  );

  const doneCount = items.filter((item) => item.ok).length;

  return (
    <div className="overview-settings-checklist">
      {loading && (
        <OverviewSettingsChecklistPreload />
      )}

      {!loading && error && (
        <p className="text-danger small mb-0">{error}</p>
      )}

      {!loading && !error && (
        <>
          <div className="overview-settings-checklist-progress mb-3">
            <span
              className={
                doneCount === items.length ? 'text-success' : 'text-secondary'
              }
            >
              {doneCount} از {items.length} مورد آماده است
            </span>
            <div
              className="overview-settings-checklist-bar"
              role="progressbar"
              aria-valuenow={doneCount}
              aria-valuemin={0}
              aria-valuemax={items.length}
            >
              <span
                style={{
                  width: `${(doneCount / items.length) * 100}%`,
                }}
              />
            </div>
          </div>

          <ul className="overview-settings-checklist-list">
            {items.map((item) => {
              const tone: CheckTone = item.ok ? 'ok' : 'warn';
              return (
                <li
                  key={item.id}
                  className={`overview-settings-check overview-settings-check--${tone}`}
                >
                  <span
                    className="overview-settings-check-mark"
                    aria-hidden
                  >
                    <IndexerIcon
                      name={
                        item.ok ? 'check-circle-fill' : 'exclamation-circle'
                      }
                    />
                  </span>
                  <span className="overview-settings-check-body">
                    <strong>{item.title}</strong>
                    <small>{item.detail}</small>
                  </span>
                  <Link
                    to={item.href}
                    className="overview-settings-check-action"
                  >
                    {item.actionLabel}
                  </Link>
                </li>
              );
            })}
          </ul>
        </>
      )}
    </div>
  );
}
